/** Ersetzt die `RemoteAuthenticatorView` aus `Pages/Auth/Auth.razor`. */

import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { msalInstance } from '@/auth/msalInstance';
import { navigateToWithReload } from '@/auth/navigation';
import { readReturnUrlFromLocation, saveReturnUrl, takeReturnUrl } from '@/auth/returnUrl';
import { useAuth } from '@/auth/useAuth';
import { API_SCOPES } from '@/config/authConfig';

const DEFAULT_MESSAGE = 'Sie werden angemeldet...';

const MESSAGES: Record<string, string> = {
  login: DEFAULT_MESSAGE,
  'login-callback': DEFAULT_MESSAGE,
  logout: 'Sie werden ausgeloggt...',
  'logout-callback': 'Sie wurden erfolgreich ausgeloggt.',
};

export function AuthCallback(): ReactElement {
  const { action } = useParams<{ action: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const handledAction = useRef<string | null>(null);

  useEffect(() => {
    if (action === undefined || handledAction.current === action) {
      return;
    }
    handledAction.current = action;

    /** Pendant zum `LoggingIn`-Fragment: Rückkehrziel sichern und Anmeldung auslösen. */
    const beginLogin = async (): Promise<void> => {
      const returnUrl = readReturnUrlFromLocation(window.location.href);
      if (returnUrl !== null && returnUrl.length > 0) {
        saveReturnUrl(returnUrl);
      }
      await msalInstance.loginRedirect({ scopes: API_SCOPES });
    };

    /** Port von `OnLoginSucceeded`. */
    const onLoginSucceeded = async (): Promise<void> => {
      try {
        const returnUrl = takeReturnUrl() ?? readReturnUrlFromLocation(window.location.href);
        const apps = await auth.loadApps();

        if (apps.length > 1 && auth.currentApp === null) {
          const query =
            returnUrl === null || returnUrl.length === 0
              ? ''
              : `?returnUrl=${encodeURIComponent(returnUrl)}`;
          void navigate(`/authentication/apps${query}`);
          return;
        }

        const [singleApp] = apps;
        if (singleApp !== undefined && apps.length === 1) {
          auth.setCurrentApp(singleApp);
          await auth.login();
        }
        navigateToWithReload(returnUrl ?? '/');
      } catch {
        void navigate('/');
      }
    };

    /** Port von `OnLogOutSucceeded`. */
    const onLogOutSucceeded = async (): Promise<void> => {
      await auth.logout();
      navigateToWithReload('/');
    };

    switch (action) {
      case 'login':
        void beginLogin();
        break;
      case 'login-callback':
        void onLoginSucceeded();
        break;
      case 'logout':
        void auth.logout();
        break;
      case 'logout-callback':
        void onLogOutSucceeded();
        break;
      default:
        break;
    }
  }, [action, auth, navigate]);

  return (
    <div style={{ textAlign: 'center', padding: '2rem' }}>
      <p>{(action === undefined ? undefined : MESSAGES[action]) ?? DEFAULT_MESSAGE}</p>
    </div>
  );
}

export default AuthCallback;
